/**
 * Chess Game Persistence Service
 * 
 * Handles saving and loading chess games including:
 * - Automatic save after moves
 * - Game metadata storage
 * - Resume from exact position
 * - Cross-device synchronization (if authenticated)
 */

import { GameState, GameMode, AIDifficulty, TimeControl } from '../types';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import { DataSynchronizationService } from '../../services/DataSynchronizationService';

export interface SavedChessGame {
  gameId: string;
  mode: GameMode;
  gameState: GameState;
  metadata: {
    players: {
      white: string;
      black: string;
    };
    aiDifficulty?: AIDifficulty;
    timeControl?: TimeControl;
    startedAt: number;
    lastSavedAt: number;
  };
}

export class ChessGamePersistence {
  private storage: LocalStorageManager;
  private syncService: DataSynchronizationService | null;
  private readonly STORAGE_KEY = 'chess_saved_games';
  private readonly AUTO_SAVE_KEY = 'chess_auto_save';

  constructor(
    storage: LocalStorageManager,
    syncService?: DataSynchronizationService
  ) {
    this.storage = storage;
    this.syncService = syncService || null;
  }

  /**
   * Save a game automatically (overwrites auto-save slot)
   */
  autoSave(
    mode: GameMode,
    gameState: GameState,
    metadata: SavedChessGame['metadata']
  ): boolean {
    try {
      const savedGame: SavedChessGame = {
        gameId: 'auto_save',
        mode,
        gameState,
        metadata: {
          ...metadata,
          lastSavedAt: Date.now()
        }
      };

      localStorage.setItem(this.AUTO_SAVE_KEY, JSON.stringify(savedGame));
      return true;
    } catch (error) {
      console.error('Failed to auto-save game:', error);
      return false;
    }
  }

  /**
   * Load the auto-saved game
   */
  loadAutoSave(): SavedChessGame | null {
    try {
      const data = localStorage.getItem(this.AUTO_SAVE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Failed to load auto-save:', error);
      return null;
    }
  }

  /**
   * Clear the auto-save slot
   */
  clearAutoSave(): boolean {
    try {
      localStorage.removeItem(this.AUTO_SAVE_KEY);
      return true;
    } catch (error) {
      console.error('Failed to clear auto-save:', error);
      return false;
    }
  }

  /**
   * Save a game with a specific ID
   */
  saveGame(
    gameId: string,
    mode: GameMode,
    gameState: GameState,
    metadata: SavedChessGame['metadata']
  ): boolean {
    try {
      const savedGame: SavedChessGame = {
        gameId,
        mode,
        gameState,
        metadata: {
          ...metadata,
          lastSavedAt: Date.now()
        }
      };

      const games = this.loadAllGames();
      const existingIndex = games.findIndex(g => g.gameId === gameId);

      if (existingIndex >= 0) {
        games[existingIndex] = savedGame;
      } else {
        games.push(savedGame);
      }

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(games));

      // TODO: Sync to cloud when DataSynchronizationService supports it
      // if (this.syncService) {
      //   this.syncService.syncData('chess_games', games);
      // }

      return true;
    } catch (error) {
      console.error('Failed to save game:', error);
      return false;
    }
  }

  /**
   * Load a specific game by ID
   */
  loadGame(gameId: string): SavedChessGame | null {
    try {
      const games = this.loadAllGames();
      return games.find(g => g.gameId === gameId) || null;
    } catch (error) {
      console.error('Failed to load game:', error);
      return null;
    }
  }

  /**
   * Load all saved games
   */
  loadAllGames(): SavedChessGame[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Failed to load games:', error);
      return [];
    }
  }

  /**
   * Delete a saved game
   */
  deleteGame(gameId: string): boolean {
    try {
      const games = this.loadAllGames();
      const filteredGames = games.filter(g => g.gameId !== gameId);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filteredGames));

      // TODO: Sync deletion to cloud when DataSynchronizationService supports it
      // if (this.syncService) {
      //   this.syncService.syncData('chess_games', filteredGames);
      // }

      return true;
    } catch (error) {
      console.error('Failed to delete game:', error);
      return false;
    }
  }

  /**
   * Get saved games by mode
   */
  getGamesByMode(mode: GameMode): SavedChessGame[] {
    try {
      const games = this.loadAllGames();
      return games.filter(g => g.mode === mode);
    } catch (error) {
      console.error('Failed to get games by mode:', error);
      return [];
    }
  }

  /**
   * Get recently saved games (last 10)
   */
  getRecentGames(limit: number = 10): SavedChessGame[] {
    try {
      const games = this.loadAllGames();
      return games
        .sort((a, b) => b.metadata.lastSavedAt - a.metadata.lastSavedAt)
        .slice(0, limit);
    } catch (error) {
      console.error('Failed to get recent games:', error);
      return [];
    }
  }

  /**
   * Check if a game exists
   */
  hasGame(gameId: string): boolean {
    return this.loadGame(gameId) !== null;
  }

  /**
   * Check if auto-save exists
   */
  hasAutoSave(): boolean {
    return this.loadAutoSave() !== null;
  }

  /**
   * Get total number of saved games
   */
  getSavedGameCount(): number {
    return this.loadAllGames().length;
  }

  /**
   * Export game to JSON string
   */
  exportGame(gameId: string): string | null {
    try {
      const game = this.loadGame(gameId);
      return game ? JSON.stringify(game, null, 2) : null;
    } catch (error) {
      console.error('Failed to export game:', error);
      return null;
    }
  }

  /**
   * Import game from JSON string
   */
  importGame(jsonString: string): boolean {
    try {
      const game: SavedChessGame = JSON.parse(jsonString);
      
      // Validate game structure
      if (!game.gameId || !game.mode || !game.gameState || !game.metadata) {
        throw new Error('Invalid game format');
      }

      return this.saveGame(game.gameId, game.mode, game.gameState, game.metadata);
    } catch (error) {
      console.error('Failed to import game:', error);
      return false;
    }
  }

  /**
   * Sync saved games from cloud (if authenticated)
   */
  async syncFromCloud(): Promise<boolean> {
    try {
      if (!this.syncService) {
        return false;
      }

      // TODO: Implement cloud sync when DataSynchronizationService supports it
      // const cloudGames = await this.syncService.fetchData('chess_games');
      // if (cloudGames) {
      //   localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cloudGames));
      //   return true;
      // }

      console.warn('Cloud sync not yet implemented for chess games');
      return false;
    } catch (error) {
      console.error('Failed to sync from cloud:', error);
      return false;
    }
  }

  /**
   * Sync saved games to cloud (if authenticated)
   */
  async syncToCloud(): Promise<boolean> {
    try {
      if (!this.syncService) {
        return false;
      }

      // TODO: Implement cloud sync when DataSynchronizationService supports it
      // const games = this.loadAllGames();
      // await this.syncService.syncData('chess_games', games);
      console.warn('Cloud sync not yet implemented for chess games');
      return true;
    } catch (error) {
      console.error('Failed to sync to cloud:', error);
      return false;
    }
  }

  /**
   * Clear all saved games
   */
  clearAllGames(): boolean {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.AUTO_SAVE_KEY);

      // TODO: Sync deletion to cloud when DataSynchronizationService supports it
      // if (this.syncService) {
      //   this.syncService.syncData('chess_games', []);
      // }

      return true;
    } catch (error) {
      console.error('Failed to clear all games:', error);
      return false;
    }
  }
}
